import React, { useEffect, useRef, useState, useCallback } from "react";
import EditorJS from "@editorjs/editorjs";
import Header from "@editorjs/header";
import List from "@editorjs/list";
import Quote from "@editorjs/quote";
import Code from "@editorjs/code";
import Embed from "@editorjs/embed";
import Table from "@editorjs/table";
import Marker from "@editorjs/marker";
import InlineCode from "@editorjs/inline-code";
import Image from "@editorjs/image";

const EditorJsComponent = ({
  data,
  onChange,
  name,
  placeholder = "Start writing your content...",
}) => {
  const editorInstance = useRef(null);
  const editorContainerRef = useRef(null);
  const wrapperRef = useRef(null);
  const isInitialized = useRef(false);
  const originalData = useRef(null);

  const [isDirty, setIsDirty] = useState(false);
  const [showButtons, setShowButtons] = useState(false);

  // Converts HTML or plain text to Editor.js blocks
  const convertToBlocks = useCallback((content) => {
    if (!content) return { time: Date.now(), blocks: [], version: "2.26.0" };

    // Already Editor.js JSON
    try {
      const parsed =
        typeof content === "string" ? JSON.parse(content) : content;
      if (parsed.blocks && Array.isArray(parsed.blocks)) return parsed;
    } catch (e) {}

    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = content;
    const blocks = [];

    const blockLevelTags = [
      "h1",
      "h2",
      "h3",
      "h4",
      "h5",
      "h6",
      "p",
      "blockquote",
      "pre",
      "ul",
      "ol",
      "table",
      "figure",
      "div",
      "section",
      "article",
    ];

    const isBlockLevel = (tag) => blockLevelTags.includes(tag.toLowerCase());

    let currentText = "";

    function traverse(node) {
      if (node.nodeType === Node.TEXT_NODE) {
        currentText += node.textContent;
        return;
      }

      if (node.nodeType !== Node.ELEMENT_NODE) return;

      const tag = node.tagName.toLowerCase();

      if (isBlockLevel(tag)) {
        // Flush pending text as paragraph
        if (currentText.trim()) {
          blocks.push({
            type: "paragraph",
            data: { text: currentText.trim() },
          });
          currentText = "";
        }

        let block = null;
        switch (tag) {
          case "h1":
          case "h2":
          case "h3":
          case "h4":
          case "h5":
          case "h6":
            const level = parseInt(tag[1]);
            const headerHtml = node.innerHTML.trim();
            if (headerHtml) {
              block = { type: "header", data: { text: headerHtml, level } };
            }
            break;
          case "p":
            const paraHtml = node.innerHTML.trim();
            if (paraHtml) {
              block = { type: "paragraph", data: { text: paraHtml } };
            }
            break;
          case "blockquote":
            const quoteHtml = node.innerHTML.trim();
            if (quoteHtml) {
              block = {
                type: "quote",
                data: { text: quoteHtml, alignment: "left" },
              };
            }
            break;
          case "pre":
            const codeText = node.textContent.trim();
            if (codeText) {
              block = { type: "code", data: { code: codeText } };
            }
            break;
          case "ul":
          case "ol":
            const style = tag === "ul" ? "unordered" : "ordered";
            const items = Array.from(node.querySelectorAll("li"))
              .map((li) => li.innerHTML.trim())
              .filter(Boolean);
            if (items.length > 0) {
              block = { type: "list", data: { style, items } };
            }
            break;
          case "table":
            const content = Array.from(node.querySelectorAll("tr"))
              .map((tr) =>
                Array.from(tr.querySelectorAll("td, th")).map((cell) =>
                  cell.innerHTML.trim()
                )
              )
              .filter((row) => row.some((cell) => cell));
            if (content.length > 0) {
              block = { type: "table", data: { content } };
            }
            break;
          case "figure":
            const img = node.querySelector("img");
            const captionEl = node.querySelector("figcaption");
            if (img?.src) {
              block = {
                type: "image",
                data: {
                  file: { url: img.src },
                  caption: captionEl?.textContent?.trim() || "",
                  withBorder: false,
                  withBackground: false,
                  stretched: false,
                },
              };
            }
            break;
          case "div":
          case "section":
          case "article":
            node.childNodes.forEach(traverse);
            if (currentText.trim()) {
              blocks.push({
                type: "paragraph",
                data: { text: currentText.trim() },
              });
              currentText = "";
            }
            return;
          default:
            const defaultHtml = node.innerHTML.trim();
            if (defaultHtml) {
              block = { type: "paragraph", data: { text: defaultHtml } };
            }
        }

        if (block) blocks.push(block);
      } else {
        node.childNodes.forEach(traverse);
      }
    }

    tempDiv.childNodes.forEach(traverse);

    // Final flush
    if (currentText.trim()) {
      blocks.push({
        type: "paragraph",
        data: { text: currentText.trim() },
      });
    }

    if (blocks.length === 0 && content.toString().trim()) {
      const plainText = content
        .toString()
        .replace(/<[^>]*>/g, "")
        .trim();
      if (plainText) {
        blocks.push({
          type: "paragraph",
          data: { text: plainText },
        });
      }
    }

    return { time: Date.now(), blocks, version: "2.26.0" };
  }, []);

  const blocksToHtml = useCallback((blocks) => {
    if (!blocks || !Array.isArray(blocks)) return "";
    console.log("Converting blocks to HTML:", blocks);

    return blocks
      .map((block) => {
        // Guard against malformed blocks
        if (!block || !block.type || !block.data) {
          console.warn("Malformed block:", block);
          return "";
        }

        const text = block.data.text || "";

        switch (block.type) {
          case "paragraph":
            return `<p class="text-base leading-relaxed text-gray-900 dark:text-gray-100">${text}</p>`;

          case "header":
            const level = block.data.level || 2;
            const headerClasses = `text-gray-900 dark:text-gray-100 font-bold leading-tight ${
              level === 1
                ? "text-4xl"
                : level === 2
                ? "text-3xl"
                : level === 3
                ? "text-2xl"
                : level === 4
                ? "text-xl"
                : level === 5
                ? "text-lg"
                : "text-base"
            }`;
            return `<h${level} class="${headerClasses}">${text}</h${level}>`;

          case "quote":
            return `<blockquote class="border-l-4 border-gray-300 dark:border-gray-600 pl-4 italic text-gray-700 dark:text-gray-300">${text}</blockquote>`;

          case "list": {
            const tag = block.data.style === "ordered" ? "ol" : "ul";
            const listClasses =
              block.data.style === "ordered"
                ? "list-decimal list-inside space-y-1 text-gray-700 dark:text-gray-300 ml-0"
                : "list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300 ml-0";

            // Debug log to see what we're working with
            console.log("List items:", block.data.items);

            // Ensure items is an array
            const items = Array.isArray(block.data.items)
              ? block.data.items
                  .map((item) => {
                    console.log("Processing item:", item, "Type:", typeof item);

                    let itemText = "";

                    // Handle different item formats
                    if (typeof item === "string") {
                      itemText = item;
                    } else if (item && typeof item === "object") {
                      // Explicitly access the content property
                      itemText = String(item.content || item.text || "");
                    } else {
                      itemText = "";
                    }

                    // Trim whitespace and newlines
                    itemText = itemText.trim();

                    console.log("Final itemText:", itemText);

                    return itemText
                      ? `<li class="text-base leading-relaxed">${itemText}</li>`
                      : "";
                  })
                  .filter((li) => li !== "")
                  .join("")
              : "";

            return items
              ? `<${tag} class="${listClasses}">${items}</${tag}>`
              : "";
          }

          case "code":
            return `<pre class="bg-gray-100 dark:bg-gray-800 rounded-lg p-4 overflow-x-auto"><code class="text-sm font-mono text-gray-800 dark:text-gray-200">${
              block.data.code || ""
            }</code></pre>`;

          case "table": {
            // Ensure content is an array
            if (!Array.isArray(block.data.content)) return "";

            const rows = block.data.content
              .map((row) => {
                if (!Array.isArray(row)) return "";
                return `<tr class="border-b border-gray-200 dark:border-gray-700">${row
                  .map(
                    (cell) =>
                      `<td class="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600">${
                        cell || ""
                      }</td>`
                  )
                  .join("")}</tr>`;
              })
              .join("");
            return `<table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800 rounded-lg overflow-hidden shadow-sm"><tbody>${rows}</tbody></table>`;
          }

          case "image": {
            const url = block.data.file?.url || block.data.url;
            const caption = block.data.caption || "";

            if (url) {
              return `<figure class="my-4 text-center"><img src="${url}" alt="${
                caption || ""
              }" class="mx-auto max-w-full h-auto rounded-lg shadow-md">${
                caption
                  ? `<figcaption class="mt-2 text-sm text-gray-600 dark:text-gray-400 italic">${caption}</figcaption>`
                  : ""
              }</figure>`;
            }
            return "";
          }

          case "delimiter":
            return `<hr class="my-8 border-t-2 border-gray-300 dark:border-gray-600" />`;

          case "warning":
            return `<div class="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-400 p-4 my-4"><p class="text-sm text-yellow-700 dark:text-yellow-300">${
              block.data.title
                ? `<strong>${block.data.title}</strong><br/>`
                : ""
            }${block.data.message || text}</p></div>`;

          default:
            // Handle unknown block types gracefully
            console.warn("Unknown block type:", block.type);
            return text
              ? `<p class="text-base leading-relaxed text-gray-900 dark:text-gray-100">${text}</p>`
              : "";
        }
      })
      .filter((html) => html !== "") // Remove empty strings
      .join("");
  }, []);
  // Editor change handler
  const handleEditorChange = useCallback(() => {
    setIsDirty(true);
    setShowButtons(true);
  }, []);

  // Initialize Editor.js
  useEffect(() => {
    if (isInitialized.current || !editorContainerRef.current) return;

    const editor = new EditorJS({
      holder: editorContainerRef.current,
      tools: {
        header: {
          class: Header,
          inlineToolbar: true,
          config: {
            placeholder: "Enter header...",
            levels: [1, 2, 3, 4, 5, 6],
            defaultLevel: 2,
          },
          shortcut: "CMD+SHIFT+H",
        },
        list: {
          class: List,
          inlineToolbar: ["bold", "italic", "link"],
        },
        quote: {
          class: Quote,
          inlineToolbar: ["bold", "italic", "link"],
        },
        code: Code,
        table: {
          class: Table,
          inlineToolbar: true,
        },
        marker: { class: Marker, shortcut: "CMD+SHIFT+M" },
        inlineCode: { class: InlineCode, shortcut: "CMD+SHIFT+C" },
        embed: Embed,
        image: {
          class: Image,
          config: {
            uploader: {
              uploadByFile(file) {
                return new Promise((resolve) => {
                  const reader = new FileReader();
                  reader.onload = (e) => {
                    resolve({
                      success: 1,
                      file: {
                        url: e.target.result,
                      },
                    });
                  };
                  reader.readAsDataURL(file);
                });
              },
              uploadByUrl(url) {
                return Promise.resolve({
                  success: 1,
                  file: { url },
                });
              },
            },
          },
        },
      },
      data: { time: 0, blocks: [], version: "2.26.0" },
      placeholder,
      minHeight: 400,
      onChange: handleEditorChange,
      logLevel: "ERROR",
    });

    editorInstance.current = editor;
    isInitialized.current = true;

    return () => {
      if (editorInstance.current?.destroy) {
        editorInstance.current.destroy();
      }
      editorInstance.current = null;
      isInitialized.current = false;
    };
  }, [placeholder, handleEditorChange]);
  // Update editor when data prop changes
  useEffect(() => {
    if (!editorInstance.current || !isInitialized.current) return;

    const newData = convertToBlocks(data);
    originalData.current = newData;
    setIsDirty(false);
    setShowButtons(false);

    editorInstance.current.isReady
      .then(() => {
        if (editorInstance.current) {
          editorInstance.current.blocks.render(newData);
        }
      })
      .catch((err) => console.error("Editor.js render error:", err));
  }, [data, convertToBlocks]);

  // Save handler
  const handleSave = useCallback(async () => {
    if (!editorInstance.current) return;
    try {
      const savedData = await editorInstance.current.saver.save();
      const htmlValue = blocksToHtml(savedData.blocks || []);
      console.log("Saved data:", savedData);
      console.log("Converted HTML:", htmlValue);
      if (typeof onChange === "function") {
        onChange({
          target: {
            name,
            value: htmlValue,
            htmlValue,
          },
        });
      }
      originalData.current = savedData;
      setIsDirty(false);
      setShowButtons(false);
    } catch (err) {
      console.error("Editor.js save error:", err);
    }
  }, [onChange, name, blocksToHtml]);

  // Cancel handler
  const handleCancel = useCallback(() => {
    if (!editorInstance.current || !originalData.current) return;
    editorInstance.current.isReady
      .then(() => {
        if (editorInstance.current) {
          editorInstance.current.blocks.render(originalData.current);
        }
      })
      .catch((err) => console.error("Editor.js cancel error:", err));
    setIsDirty(false);
    setShowButtons(false);
  }, []);

  return (
    <div ref={wrapperRef} className="relative">
      <div
        ref={editorContainerRef}
        className="border border-gray-300 rounded-lg pt-14 px-3 pb-3 min-h-[400px] bg-white mb-3"
      />
      {showButtons && (
        <div
          style={{
            display: "flex",
            gap: "8px",
            zIndex: 10,
          }}
        >
          <button
            onClick={handleSave}
            disabled={!isDirty}
            style={{
              padding: "8px 16px",
              backgroundColor: "#2563eb", // blue-600
              color: "#ffffff",
              fontWeight: "500",
              borderRadius: "4px",
              border: "none",
              cursor: isDirty ? "pointer" : "not-allowed",
              transition: "background-color 0.2s",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.backgroundColor = "#1d4ed8")
            } // blue-700
            onMouseLeave={(e) =>
              (e.currentTarget.style.backgroundColor = "#2563eb")
            }
          >
            Save
          </button>

          <button
            onClick={handleCancel}
            style={{
              padding: "8px 16px",
              backgroundColor: "#d1d5db", // gray-300 // gray-800
              fontWeight: "500",
              borderRadius: "4px",
              border: "none",
              cursor: "pointer",
              transition: "background-color 0.2s",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.backgroundColor = "#9ca3af")
            } // gray-400
            onMouseLeave={(e) =>
              (e.currentTarget.style.backgroundColor = "#d1d5db")
            }
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
};

export default EditorJsComponent;
